// frontend/src/components/nest/DayTimeline.js
import { useCallback, useRef, useState } from 'react';
import { Plus } from 'lucide-react';
import { timeToMinutes, minutesToTime } from '@/lib/nestTimeline';
import ActivityBlock from './ActivityBlock';

const HOUR_HEIGHT_PX = 56;
const SNAP_MINUTES = 5;
const DRAG_THRESHOLD_PX = 4; // below this, a pointerdown+up is treated as a click, not a drag

export default function DayTimeline({ childrenData, dayStartTime = '08:00', dayEndTime = '17:00', onAddActivity, onEditItem, onMoveItem }) {
  const startMin = timeToMinutes(dayStartTime);
  const endMin = timeToMinutes(dayEndTime);
  const totalHours = Math.ceil((endMin - startMin) / 60);
  const hours = Array.from({ length: totalHours }, (_, i) => Math.floor(startMin / 60) + i);
  const totalHeightPx = totalHours * HOUR_HEIGHT_PX;
  const pxPerMinute = HOUR_HEIGHT_PX / 60;
  const snapPx = SNAP_MINUTES * pxPerMinute;

  const topFor = (time) => ((timeToMinutes(time) - startMin) / 60) * HOUR_HEIGHT_PX;
  const heightFor = (start, end) => Math.max(((timeToMinutes(end) - timeToMinutes(start)) / 60) * HOUR_HEIGHT_PX, 24);

  // Drag-to-reschedule: click still opens the popover (manual time entry stays
  // available as the other option), a real drag calls onMoveItem instead.
  const [dragId, setDragId] = useState(null);
  const [dragTop, setDragTop] = useState(0);
  const dragRef = useRef(null);
  // Browsers fire a native click on the button after pointerup even when the
  // pointer moved a lot in between, as long as it's released over the same
  // element (which it usually is mid-drag) — this flag tells that click to
  // no-op instead of popping the popover open with the stale pre-drag time.
  const justDraggedRef = useRef(false);

  const beginDrag = useCallback((e, item) => {
    if (e.button !== 0 && e.button !== undefined) return; // primary button/touch only
    const startTop = ((timeToMinutes(item.start_time) - startMin) / 60) * HOUR_HEIGHT_PX;
    const itemHeight = Math.max(((timeToMinutes(item.end_time) - timeToMinutes(item.start_time)) / 60) * HOUR_HEIGHT_PX, 24);
    const state = { item, startTop, startClientY: e.clientY, currentTop: startTop, moved: false, itemHeight };
    dragRef.current = state;
    setDragId(item.id);
    setDragTop(startTop);

    const handleMove = (moveEvent) => {
      const s = dragRef.current;
      if (!s) return;
      const deltaY = moveEvent.clientY - s.startClientY;
      if (Math.abs(deltaY) > DRAG_THRESHOLD_PX) s.moved = true;
      let newTop = s.startTop + deltaY;
      newTop = Math.max(0, Math.min(newTop, totalHeightPx - s.itemHeight));
      newTop = Math.round(newTop / snapPx) * snapPx;
      s.currentTop = newTop;
      setDragTop(newTop);
    };

    const finishDrag = () => {
      window.removeEventListener('pointermove', handleMove);
      window.removeEventListener('pointerup', finishDrag);
      const s = dragRef.current;
      dragRef.current = null;
      setDragId(null);
      if (!s) return;
      if (!s.moved) {
        onEditItem(s.item);
        return;
      }
      justDraggedRef.current = true;
      setTimeout(() => { justDraggedRef.current = false; }, 0); // clears after the trailing click event fires
      const newMinutes = Math.round(startMin + s.currentTop / pxPerMinute);
      onMoveItem(s.item, minutesToTime(newMinutes));
    };

    window.addEventListener('pointermove', handleMove);
    window.addEventListener('pointerup', finishDrag);
  }, [totalHeightPx, snapPx, pxPerMinute, startMin, onEditItem, onMoveItem]);

  return (
    <div className="flex border border-sand-darker rounded-2xl overflow-y-auto max-h-[80vh] bg-white" data-testid="day-timeline">
      <div className="w-14 flex-shrink-0 border-r border-sand-darker">
        <div className="h-10 border-b border-sand-darker" />
        {hours.map(h => (
          <div key={h} style={{ height: HOUR_HEIGHT_PX, transform: 'translateY(-8px)' }} className="text-[10px] text-slate-400 text-right pr-1.5">
            {h.toString().padStart(2, '0')}:00
          </div>
        ))}
      </div>
      {childrenData.map(child => (
        <div key={child.name} className="flex-1 min-w-[160px] border-r border-sand-darker last:border-r-0">
          <div className="h-10 flex items-center justify-between px-2.5 text-sm font-semibold border-b border-sand-darker" data-testid={`day-lane-header-${child.name}`}>
            <span className="truncate">{child.name}</span>
            <button
              onClick={() => onAddActivity(child.name)}
              className="flex-shrink-0 w-6 h-6 flex items-center justify-center rounded-full text-moana hover:bg-moana-50 hover:text-moana-dark transition"
              aria-label={`Add activity for ${child.name}`}
              data-testid={`day-lane-add-${child.name}`}
            >
              <Plus className="w-4 h-4" />
            </button>
          </div>
          <div className="relative" style={{ height: totalHours * HOUR_HEIGHT_PX }}>
            {child.items.length === 0 && (
              <button
                onClick={() => onAddActivity(child.name)}
                className="absolute inset-2 rounded-lg border-2 border-dashed border-sand-darker text-xs text-slate-400 hover:border-fern hover:text-fern"
                data-testid={`day-lane-empty-${child.name}`}
              >
                + Add activity
              </button>
            )}
            {child.items.map(item => {
              const isDragging = dragId === item.id;
              const top = isDragging ? dragTop : topFor(item.start_time);
              return (
                <div
                  key={item.id}
                  className={`absolute left-1 right-1 touch-none ${isDragging ? 'z-10 shadow-lg cursor-grabbing' : 'cursor-grab'}`}
                  style={{ top, height: heightFor(item.start_time, item.end_time) }}
                  onPointerDown={(e) => beginDrag(e, item)}
                  data-testid={`activity-drag-handle-${item.id}`}
                >
                  <ActivityBlock item={{ ...item, color: item.color || child.color }} onClick={() => { if (justDraggedRef.current) return; onEditItem(item); }} />
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}
